import { useCallback, useEffect, useState } from "react";
import { VoiceTextarea } from "../components/VoiceTextarea";
import { DebugLog } from "../debug/log";
import { getIdea, putIdea } from "../data/ideaStore";
import type { ModelInfo } from "../data/modelStore";
import {
  type Clarification,
  type Idea,
  type Settings,
  clarificationsOf,
} from "../data/types";
import { activeEngineLabel, getActiveEngine } from "../engine/active";
import {
  MOM_TEST_SYSTEM_PROMPT,
  buildClarifyUserMessage,
  parseClarifyResponse,
} from "../engine/prompts";

const TAG = "ui.clarify";

interface Props {
  ideaId: string;
  settings: Settings;
  installedModel: ModelInfo | null;
  onBack: () => void;
  onIdeaSaved: () => void;
}

type FlowState =
  | { kind: "loading" }
  | { kind: "noEngine" }
  | { kind: "noIdea" }
  | { kind: "ready"; idea: Idea }
  | { kind: "thinking"; idea: Idea }
  | { kind: "asking"; idea: Idea; question: string }
  | { kind: "done"; idea: Idea; summary: string }
  | { kind: "error"; idea: Idea; message: string };

export function ClarifyScreen(props: Props) {
  const [state, setState] = useState<FlowState>({ kind: "loading" });
  const [answer, setAnswer] = useState("");

  const askNextQuestion = useCallback(
    async (idea: Idea) => {
      setState({ kind: "thinking", idea });
      const engine = getActiveEngine(props.settings, props.installedModel);
      if (!engine) {
        setState({ kind: "error", idea, message: "No engine configured." });
        return;
      }
      const clarifications = clarificationsOf(idea);
      DebugLog.i(
        TAG,
        "engine call: next clarifying question",
        `ideaId=${idea.id} clarificationsSoFar=${clarifications.length}`,
      );
      const result = await engine.complete({
        messages: [
          { role: "system", content: MOM_TEST_SYSTEM_PROMPT },
          {
            role: "user",
            content: buildClarifyUserMessage({
              title: idea.title,
              rawCapture: idea.rawCapture,
              clarifications,
            }),
          },
        ],
        maxTokens: 512,
        temperature: 0.3,
      });
      if (result.kind === "error") {
        DebugLog.w(TAG, `engine returned error: ${result.message}`);
        setState({ kind: "error", idea, message: result.message });
        return;
      }
      DebugLog.i(TAG, `engine replied (${result.text.length} chars)`, result.text.slice(0, 200));
      const reply = parseClarifyResponse(result.text);
      if (reply.kind === "done") {
        setState({ kind: "done", idea, summary: reply.summary || "Clarification complete." });
        return;
      }
      setState({ kind: "asking", idea, question: reply.text });
    },
    [props.settings, props.installedModel],
  );

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const idea = await getIdea(props.ideaId);
      if (cancelled) return;
      if (!idea) {
        setState({ kind: "noIdea" });
        return;
      }
      const engine = getActiveEngine(props.settings, props.installedModel);
      if (!engine) {
        setState({ kind: "noEngine" });
        return;
      }
      // Resume mid-conversation when the idea already has clarifications.
      if (clarificationsOf(idea).length === 0) {
        setState({ kind: "ready", idea });
      } else {
        await askNextQuestion(idea);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [props.ideaId, props.settings, props.installedModel, askNextQuestion]);

  const handleStart = async () => {
    if (state.kind !== "ready") return;
    await askNextQuestion(state.idea);
  };

  const handleSubmitAnswer = async () => {
    if (state.kind !== "asking") return;
    const trimmed = answer.trim();
    if (trimmed.length === 0) return;

    const updated: Idea = {
      ...state.idea,
      clarifications: [
        ...clarificationsOf(state.idea),
        {
          question: state.question,
          answer: trimmed,
          timestampMs: Date.now(),
        } satisfies Clarification,
      ],
      updatedAtMs: Date.now(),
    };
    await putIdea(updated);
    props.onIdeaSaved();
    setAnswer("");
    await askNextQuestion(updated);
  };

  const handleFinishEarly = () => {
    if (
      state.kind === "asking" ||
      state.kind === "thinking" ||
      state.kind === "error"
    ) {
      setState({
        kind: "done",
        idea: state.idea,
        summary: "Clarification stopped early — answers so far are saved.",
      });
    }
  };

  return (
    <>
      <header className="sticky top-0 z-10 flex items-center justify-between gap-2 border-b border-black/5 bg-bg/90 px-4 py-3 backdrop-blur">
        <button className="btn-ghost" onClick={props.onBack}>
          Back
        </button>
        <h1 className="text-lg font-semibold tracking-tight">Clarify</h1>
        {state.kind === "asking" || state.kind === "thinking" ? (
          <button className="btn-ghost" onClick={handleFinishEarly}>
            Finish
          </button>
        ) : (
          <span className="w-16" />
        )}
      </header>

      <main className="flex flex-1 flex-col gap-4 px-4 py-4">
        {state.kind === "loading" && (
          <div className="text-sm text-ink-muted">Loading…</div>
        )}

        {state.kind === "noIdea" && (
          <div className="rounded-xl bg-rose-50 p-4 text-sm text-rose-900 ring-1 ring-rose-200">
            <div className="font-semibold">That idea is gone</div>
            <button className="btn-ghost mt-2" onClick={props.onBack}>
              Back
            </button>
          </div>
        )}

        {state.kind === "noEngine" && (
          <div className="rounded-xl bg-rose-50 p-4 text-sm text-rose-900 ring-1 ring-rose-200">
            <div className="font-semibold">No LLM engine configured</div>
            <p className="mt-1">
              Open Settings and either enable the cloud LLM with an API key, or install the
              on-device model. Then come back here.
            </p>
          </div>
        )}

        {(state.kind === "ready" ||
          state.kind === "thinking" ||
          state.kind === "asking" ||
          state.kind === "done" ||
          state.kind === "error") && (
          <>
            <IdeaHeader
              idea={state.idea}
              settings={props.settings}
              installedModel={props.installedModel}
            />
            <HistoryList items={clarificationsOf(state.idea)} />

            {state.kind === "ready" && (
              <div className="card flex flex-col gap-3 p-4">
                <p className="text-sm text-ink-muted">
                  Mom-Test discipline: I'll ask one question at a time about past behaviour,
                  current alternatives, and dealbreakers. After 5–8 useful answers I'll wrap up.
                </p>
                <button className="btn-primary" onClick={handleStart}>
                  Start clarifying
                </button>
              </div>
            )}

            {state.kind === "thinking" && (
              <div className="card flex flex-col gap-2 p-4">
                <div className="text-sm font-medium">Thinking…</div>
                <div className="text-xs text-ink-muted">
                  First on-device turn loads Gemma into GPU memory — this can take a few seconds.
                </div>
              </div>
            )}

            {state.kind === "asking" && (
              <div className="flex flex-col gap-3">
                <div className="rounded-xl bg-accent-soft p-4 text-sm ring-1 ring-accent/20">
                  <div className="text-xs font-semibold uppercase tracking-wide text-accent">
                    Question {clarificationsOf(state.idea).length + 1}
                  </div>
                  <div className="mt-1 whitespace-pre-wrap text-ink">{state.question}</div>
                </div>
                <VoiceTextarea
                  value={answer}
                  onChange={setAnswer}
                  placeholder="Your answer — be specific, lean on past behaviour over hypotheticals."
                  minHeightClass="min-h-[120px]"
                  hideDisclosure
                />
                <button
                  className="btn-primary"
                  disabled={answer.trim().length === 0}
                  onClick={handleSubmitAnswer}
                >
                  Save answer & continue
                </button>
              </div>
            )}

            {state.kind === "done" && (
              <div className="rounded-xl bg-accent-soft p-4 text-sm ring-1 ring-accent/20">
                <div className="text-xs font-semibold uppercase tracking-wide text-accent">
                  Clarification complete
                </div>
                <div className="mt-1 whitespace-pre-wrap text-ink">{state.summary}</div>
                <button className="btn-ghost mt-3" onClick={props.onBack}>
                  Back to idea
                </button>
              </div>
            )}

            {state.kind === "error" && (
              <div className="rounded-xl bg-rose-50 p-4 text-sm text-rose-900 ring-1 ring-rose-200">
                <div className="font-semibold">Engine error</div>
                <p className="mt-1 whitespace-pre-wrap">{state.message}</p>
                <button className="btn-ghost mt-3" onClick={() => askNextQuestion(state.idea)}>
                  Retry
                </button>
              </div>
            )}
          </>
        )}
      </main>
    </>
  );
}

function IdeaHeader(props: {
  idea: Idea;
  settings: Settings;
  installedModel: ModelInfo | null;
}) {
  const label = activeEngineLabel(props.settings, props.installedModel);
  return (
    <section className="flex flex-col gap-1">
      <h2 className="text-base font-semibold">{props.idea.title}</h2>
      <div className="text-xs text-ink-muted">Engine: {label}</div>
    </section>
  );
}

function HistoryList(props: { items: Clarification[] }) {
  if (props.items.length === 0) return null;
  return (
    <section className="flex flex-col gap-2">
      <h3 className="text-sm font-semibold text-ink-muted">Answered so far</h3>
      <ul className="flex flex-col gap-2">
        {props.items.map((c, idx) => (
          <li key={`${c.timestampMs}-${idx}`} className="card p-3">
            <div className="text-xs font-semibold text-ink-muted">Q{idx + 1}</div>
            <div className="text-sm">{c.question}</div>
            <div className="mt-2 text-xs font-semibold text-ink-muted">A{idx + 1}</div>
            <div className="whitespace-pre-wrap text-sm">{c.answer}</div>
          </li>
        ))}
      </ul>
    </section>
  );
}
